import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { apiTrustShow } from "../lib/api";

type TrustShowResp = {
  ok: boolean;
  trust: { id: number; trust_name: string; status: string };
  eurBalance: number;
  eurAvailable: number;
  cryptoCodes: string[];
  cryptoBalances: Record<string, number>;
  cryptoLocked: Record<string, number>;
  cryptoAvailable: Record<string, number>;
  wishes: Array<{
    id: number;
    type: "buy" | "sell" | "withdraw";
    from_asset?: string | null;
    to_asset?: string | null;
    amount: number | string;
    final_amount: number | string;
    status: "pending" | "executed" | "rejected";
  }>;
};

export default function TrustsShow() {
  const { id } = useParams<{ id?: string }>();
  const trustId = Number(id);
  const idValid = Boolean(id) && Number.isFinite(trustId) && trustId > 0;

  const [data, setData] = React.useState<TrustShowResp | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!idValid) {
      setLoading(false);
      return;
    }

    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res: TrustShowResp = await apiTrustShow(trustId);
        if (alive) setData(res);
      } catch (e: any) {
        if (alive) setErr(e?.message || "Failed to load trust");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [idValid, trustId]);

  if (!idValid) {
    return <Navigate to="/trusts" replace />;
  }

  if (loading) {
    return (
      <div className="section-box p-4 sm:p-6">
        <div className="skeleton h-32" />
      </div>
    );
  }

  if (err || !data?.ok) {
    return (
      <div className="section-box p-4 sm:p-6">
        <div className="alert-danger">{err ?? "Failed to load trust"}</div>
      </div>
    );
  }

  const t = data.trust;
  const eurBalance = Number(data.eurBalance ?? 0);
  const eurAvailable = Number(data.eurAvailable ?? 0);

  return (
    <div className="section-box p-4 sm:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t.trust_name}</h1>
          <div className="text-sm text-gray-600">
            Status:{" "}
            <span
              className={`chip chip-${
                t.status === "active"
                  ? "success"
                  : t.status === "pending"
                  ? "warning"
                  : t.status === "rejected"
                  ? "danger"
                  : "default"
              }`}
            >
              {t.status}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link className="btn btn-white" to={`/trusts/${t.id}/wishes/create`}>
            Create wish
          </Link>
          <Link className="btn" to={`/trust/transfer`}>Transfer EUR</Link>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-box">
          <div className="text-sm text-gray-500 mb-1">EUR balance</div>
          <div className="text-2xl font-semibold">€{eurBalance.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            Available (after pending BUY): €{eurAvailable.toLocaleString()}
          </div>
        </div>
        <div className="card-box">
          <div className="text-sm text-gray-500 mb-2">Crypto balances</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            {data.cryptoCodes.map((c: string) => (
              <div key={c} className="p-2 rounded bg-gray-50">
                <div className="font-medium">{c}</div>
                <div className="text-xs">Gross: {data.cryptoBalances[c] ?? 0}</div>
                <div className="text-xs">Locked: {data.cryptoLocked[c] ?? 0}</div>
                <div className="text-xs">Avail: {data.cryptoAvailable[c] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-box">
        <div className="text-sm font-semibold mb-2">Recent wishes</div>
        {data.wishes.length === 0 ? (
          <div className="text-sm text-gray-600">No wishes yet.</div>
        ) : (
          <div className="space-y-2">
            {data.wishes.map((w) => (
              <div
                key={w.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm border rounded-lg p-2"
              >
                <div>
                  <div className="font-medium">
                    {w.type.toUpperCase()} {w.from_asset} → {w.to_asset || "-"}
                  </div>
                  <div className="text-gray-600">
                    Amount: {w.amount} • Final: {w.final_amount} • Status:{" "}
                    <span
                      className={`chip chip-${
                        w.status === "pending"
                          ? "warning"
                          : w.status === "executed"
                          ? "success"
                          : w.status === "rejected"
                          ? "danger"
                          : "default"
                      }`}
                    >
                      {w.status}
                    </span>
                  </div>
                </div>
                <Link className="btn btn-white" to={`/trusts/wishes/${w.id}`}>
                  View
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
